import { AxiosInstance } from "axios"
import {
    BookingsSummaryReport,
    BookingSummaryReport,
    CancellationRateReport,
    GuestsSummaryReport,
    OccupancyRateReport,
    OccupancyRateSummary,
    PeakHoursSummaryReport,
    UsagePerTenantPerResourceReport,
    UsagePerTenantSummaryReport,
} from "./statisticsTypes"
import { ResourceType } from "../../admin/organization/resources/resourcesTypes"

export enum ServiceCenterStatisticsQuery {
    SERVICE_CENTER_STATISTICS_OCCUPANCY_RATE = "SERVICE_CENTER_STATISTICS_OCCUPANCY_RATE",
    SERVICE_CENTER_STATISTICS_OCCUPANCY_RATE_PER_RESOURCE = "SERVICE_CENTER_STATISTICS_OCCUPANCY_RATE_PER_RESOURCE",
    SERVICE_CENTER_STATISTICS_CANCELLATION_RATE_PER_RESOURCE = "SERVICE_CENTER_STATISTICS_CANCELLATION_RATE_PER_RESOURCE",
    SERVICE_CENTER_STATISTICS_BOOKINGS_SUMMARY = "SERVICE_CENTER_STATISTICS_BOOKINGS_SUMMARY",
    SERVICE_CENTER_STATISTICS_BOOKINGS_SUMMARY_PER_RESOURCE = "SERVICE_CENTER_STATISTICS_BOOKINGS_SUMMARY_PER_RESOURCE",
    SERVICE_CENTER_STATISTICS_GUESTS_SUMMARY = "SERVICE_CENTER_STATISTICS_GUESTS_SUMMARY",
    SERVICE_CENTER_STATISTICS_PEAK_HOURS_SUMMARY = "SERVICE_CENTER_STATISTICS_PEAK_HOURS_SUMMARY",
    SERVICE_CENTER_STATISTICS_USAGE_PER_TENANT_SUMMARY = "SERVICE_CENTER_STATISTICS_USAGE_PER_TENANT_SUMMARY",
    SERVICE_CENTER_STATISTICS_USAGE_PER_TENANT_PER_RESOURCE = "SERVICE_CENTER_STATISTICS_USAGE_PER_TENANT_PER_RESOURCE",
}

export type ServiceCenterOccupancyRateReportParams = Readonly<{
    serviceCenterId: number
    startDate: string
    endDate: string
    resourceType: ResourceType
}>

export type ServiceCenterOccupancyRateReportResponse = OccupancyRateReport

export type ServiceCenterOccupancyRateSummaryParams = Readonly<{
    serviceCenterId: number
    startDate: string
    endDate: string
}>

export type ServiceCenterOccupancyRateSummaryResponse = OccupancyRateSummary

export async function fetchOccupancyRate(
    params: ServiceCenterOccupancyRateSummaryParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterOccupancyRateSummaryResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/occupancyRate`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

export async function fetchOccupancyRatePerResource(
    params: ServiceCenterOccupancyRateReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterOccupancyRateReportResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/occupancyRatePerResource`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

export type ServiceCenterCancellationRateReportResponse = CancellationRateReport
type ServiceCenterCancellationRateReportParams = ServiceCenterOccupancyRateReportParams

export async function fetchCancellationRatePerResource(
    params: ServiceCenterCancellationRateReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterCancellationRateReportResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/cancellationRatePerResource`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

export type ServiceCenterBookingsSummaryParams = Readonly<{
    serviceCenterId: number
    startDate: string
    endDate: string
}>

export type ServiceCenterBookingsSummaryResponse = BookingsSummaryReport

export async function fetchBookingsSummary(
    params: ServiceCenterBookingsSummaryParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterBookingsSummaryResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/bookingsSummary`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

export type ServiceCenterBookingSummaryReportResponse = BookingSummaryReport
type ServiceCenterBookingSummaryRateReportParams = ServiceCenterOccupancyRateReportParams

export async function fetchBookingSummaryRatePerResource(
    params: ServiceCenterBookingSummaryRateReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterBookingSummaryReportResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/bookingsSummaryPerResource`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

type ServiceCenterGuestsReportParams = Readonly<{
    serviceCenterId: number
    startDate: string
    endDate: string
}>

export type ServiceCenterGuestsSummaryReportResponse = GuestsSummaryReport
type ServiceCenterGuestsSummaryRateReportParams = ServiceCenterGuestsReportParams

export async function fetchGuestsSummaryRatePerResource(
    params: ServiceCenterGuestsSummaryRateReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterGuestsSummaryReportResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/guestsSummary`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

export type ServiceCenterPeakHoursSummaryReportResponse = PeakHoursSummaryReport
type ServiceCenterPeakHoursSummaryRateReportParams = ServiceCenterOccupancyRateReportParams

export async function fetchPeakHoursSummaryRatePerResource(
    params: ServiceCenterPeakHoursSummaryRateReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterPeakHoursSummaryReportResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/hourlyOccupancyRatePerResource`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

// `resourceType` is optional: when omitted (the "All types" filter), useApiQuery's
// filterEmptyParams drops it and the backend returns all resource types.
// `companyId` is optional too: sent only when a tenant is selected in the filter.
type ServiceCenterUsagePerTenantReportParams = Readonly<{
    serviceCenterId: number
    startDate: string
    endDate: string
    resourceType?: ResourceType
    companyId?: number
}>

export type ServiceCenterUsagePerTenantSummaryResponse = UsagePerTenantSummaryReport

export async function fetchUsagePerTenantSummary(
    params: ServiceCenterUsagePerTenantReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterUsagePerTenantSummaryResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/usagePerTenant`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

export type ServiceCenterUsagePerTenantPerResourceResponse = UsagePerTenantPerResourceReport

export async function fetchUsagePerTenantPerResource(
    params: ServiceCenterUsagePerTenantReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterUsagePerTenantPerResourceResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/usagePerTenantPerResource`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}
